import DataLoader from "@-/com.npmjs.dataloader";
import { scheduleDataloaderBatch } from "#schedule-dataloader-batch";

export type BatchLoadFn<K, V> = (keys: readonly K[]) => Promise<readonly V[]>;

const DEFAULT_MAX_BATCH_SIZE = 64;

/**
 * Coalesce `.load(key)` calls across React commits within one animation frame
 * (≈16ms on 60fps), instead of just within a single microtask tick. Disabling
 * this reverts to DataLoader's default microtask scheduler.
 *
 * Why this exists: React 19 concurrent rendering commits children incrementally,
 * so a Browse page mounting N morpheme buttons fires N separate `useQuery`s
 * across N commits → N separate DataLoader batches → each serialized on
 * expo-sqlite's per-DB thread (one bridge round trip each). Frame-aligned
 * batching collapses a render burst into one batch, eliminating the queue.
 *
 * Trade-off: a single isolated `.load()` now waits one frame (~16ms) instead of
 * one microtask. Set to `false` to A/B against the default behavior.
 */
const COALESCE_DATALOADER_BATCHES_ACROSS_COMMITS = true;

/**
 * Resolve a frame-aligned batch scheduler: the next animation frame (React
 * Native + browsers), or `request-animation-frames`' ~16ms timer in server /
 * test environments without frames. Returns `undefined` when coalescing is
 * disabled, so DataLoader uses its default microtask scheduler.
 */
function getBatchScheduleFn(): ((callback: () => void) => void) | undefined {
  if (!COALESCE_DATALOADER_BATCHES_ACROSS_COMMITS) {
    return undefined;
  }
  return scheduleDataloaderBatch;
}

/**
 * Module-scope factory for a per-dependency DataLoader. Each distinct `dep`
 * (e.g. a database handle) maps to its own loader; calls to `load(key)` from
 * any consumer holding the same `dep` coalesce into a single batched
 * `batchLoad` within the configured scheduling window (see
 * {@link COALESCE_DATALOADER_BATCHES_ACROSS_COMMITS}).
 *
 * Why this exists: a plain `WHERE key IN (...)` over thousands of keys defeats
 * SQLite's per-column index and triggers a full table scan. DataLoader's bounded
 * batches keep the planner on the index. Pairing batched fetching with
 * per-key Query caching (see `useBatchedQueryOptions`) makes the lookup
 * virtualization-aware: only keys whose components actually mount are fetched.
 *
 * The `WeakMap` keys ensure loaders are GC'd when the dependency is no longer
 * referenced (e.g. a DB handle swaps out).
 *
 * `cache: false` because Query owns per-key caching at a higher layer — caching
 * inside DataLoader would shadow Query's invalidation.
 */
export function createDataloader<Dep extends object, K, V>(
  makeBatchLoad: (dep: Dep) => BatchLoadFn<K, V>,
  options?: { maxBatchSize?: number },
): (dep: Dep) => DataLoader<K, V> {
  const cache = new WeakMap<Dep, DataLoader<K, V>>();
  const maxBatchSize = options?.maxBatchSize ?? DEFAULT_MAX_BATCH_SIZE;
  const batchScheduleFn = getBatchScheduleFn();
  return (dep) => {
    let loader = cache.get(dep);
    if (loader === undefined) {
      loader = new DataLoader<K, V>(async (keys) => makeBatchLoad(dep)(keys), {
        batchScheduleFn,
        cache: false,
        maxBatchSize,
      });
      cache.set(dep, loader);
    }
    return loader;
  };
}
